// src/pages/SignUpCustomer.js
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API from '../api';
import ReCAPTCHA from 'react-google-recaptcha';
import { AuthContext } from '../components/AuthContext';

function SignUpCustomer() {
  const navigate = useNavigate();
  const { isLoggedIn } = useContext(AuthContext);

  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [newsletter, setNewsletter] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [message, setMessage] = useState('');

  // Controlla se c'è un redirect target salvato (dopo migrazione)
  useEffect(() => {
    if (isLoggedIn) {
      const redirectTarget = localStorage.getItem('signup_redirect_target');
      if (redirectTarget) {
        console.log('[SignUp] Trovato redirect target salvato:', redirectTarget);
        localStorage.removeItem('signup_redirect_target'); // Rimuovi il target salvato
        navigate(redirectTarget);
      }
    }
  }, [isLoggedIn, navigate]);

  // Questo handleSubmit manderà anche il captchaToken al backend
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setMessage('Le password non coincidono!');
      return;
    }

    if (!captchaToken) {
      setMessage('Per favore completa il reCAPTCHA.');
      return;
    }

    try {
      const response = await API.post('/auth/signup-customer', {
        name,
        lastName,
        email,
        password,
        newsletter,
        captchaToken
      });
      if (response.data.success) {
        // Registrazione OK => In automatico l'API fa login e restituisce user info o token
        setMessage('Registrazione completata, reindirizzamento in corso...');
        
        // Migra la wishlist guest se esiste
        let migrationHappened = false;
        try {
          const session_id = localStorage.getItem('wishlist_session_id');
          if (session_id) {
            await API.post('/wishlist/migrate', { session_id });
            console.log('[SignUp] Wishlist guest migrata con successo');
            migrationHappened = true;
            // Rimuovi il session_id dopo la migrazione
            localStorage.removeItem('wishlist_session_id');
          }
        } catch (migrationError) {
          console.error('[SignUp] Errore nella migrazione wishlist:', migrationError);
          // Non blocchiamo la registrazione se la migrazione fallisce
        }
        
        // Se è avvenuta una migrazione, salva il redirect target e ricarica
        if (migrationHappened) {
          console.log('[SignUp] Migrazione avvenuta, salvo il redirect target e ricarico');
          
          // Salva il redirect target nel localStorage (homepage per registrazione)
          localStorage.setItem('signup_redirect_target', '/');
          
          // Ricarica la pagina
          window.location.reload();
          return;
        }
        
        // Se l'API ritorna un token, potresti salvarlo:
        // localStorage.setItem('token', response.data.token);
        // E poi reindirizzi alla homepage
        navigate('/');
      }
    } catch (error) {
      setMessage(
        error.response
          ? error.response.data.message
          : 'Errore di rete, riprova.'
      );
    }
  };

  // Quando l'utente risolve il captcha, Google ci dà un token
  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  return (
    <div className="signup-page">
      <div className="signup-container">
        {/* Header con icona */}
        <div className="signup-header">
          <div className="signup-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="m22 21-2-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="m16 16 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="signup-title">Crea il tuo account</h2>
          <p className="signup-subtitle">Unisciti a noi e inizia a fare acquisti</p>
        </div>

        <form onSubmit={handleSubmit} className="signup-form">
          {/* Nome e Cognome in riga */}
          <div className="signup-row">
            <div className="signup-form-group">
              <label className="signup-form-label">Nome</label>
              <div className="signup-input-wrapper">
                <svg className="signup-input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="text"
                  className="signup-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Il tuo nome"
                  required
                />
              </div>
            </div>
            <div className="signup-form-group">
              <label className="signup-form-label">Cognome</label>
              <div className="signup-input-wrapper">
                <svg className="signup-input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="text"
                  className="signup-input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Il tuo cognome"
                  required
                />
              </div>
            </div>
          </div>

          {/* Email */}
          <div className="signup-form-group">
            <label className="signup-form-label">Email</label>
            <div className="signup-input-wrapper">
              <svg className="signup-input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="email"
                className="signup-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="La tua email"
                required
              />
            </div>
          </div>

          {/* Password e Conferma Password in riga */}
          <div className="signup-row">
            <div className="signup-form-group">
              <label className="signup-form-label">Password</label>
              <div className="signup-input-wrapper">
                <svg className="signup-input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="16" r="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="password"
                  className="signup-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Crea una password"
                  required
                />
              </div>
            </div>
            <div className="signup-form-group">
              <label className="signup-form-label">Conferma Password</label>
              <div className="signup-input-wrapper">
                <svg className="signup-input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="16" r="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="password"
                  className="signup-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Conferma la password"
                  required
                />
              </div>
            </div>
          </div>

          {/* Newsletter checkbox */}
          <div className="signup-checkbox-group">
            <label className="signup-checkbox-label">
              <input
                type="checkbox"
                className="signup-checkbox"
                checked={newsletter}
                onChange={(e) => setNewsletter(e.target.checked)}
              />
              <span className="signup-checkbox-custom"></span>
              <span className="signup-checkbox-text">Iscrivimi alla newsletter</span>
            </label>
          </div>

          {/* RECAPTCHA */}
          <div className="signup-captcha">
            <ReCAPTCHA
              sitekey="6LcjTrgqAAAAAARpF7ZadLrB7z6g3ZiTNS_0DMtu"
              onChange={handleCaptchaChange}
            />
          </div>

          <button type="submit" className="signup-submit-btn">
            <span>Crea Account</span>
            <svg className="signup-submit-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="m12 5 7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </form>

        {message && (
          <div className="signup-message">
            <svg className="signup-message-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="m9 12 2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {message}
          </div>
        )}

        {/* Footer con link login */}
        <div className="signup-footer">
          <p className="signup-footer-text">Hai già un account?</p>
          <Link to="/login" className="signup-login-link">
            Accedi ora
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 17 17 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 7h10v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default SignUpCustomer;
